import Component from '@glimmer/component';
import { union, sort } from '@ember/object/computed';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { task, timeout } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { A } from '@ember/array';

import ActivityDay from 'seshy/faux-models/activity/day';
import ActivityStateChange from 'seshy/faux-models/activity/state-change';

export default class ProjectsActivityListComponent extends Component {
  @service store;

  @tracked activityData = A([]);

  compactCalendarFormatHash = {
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    nextWeek: 'dddd',
    lastDay: '[Yesterday]',
    lastWeek: '[Last] dddd MMM Do YYYY',
    sameElse: 'ddd MMM Do YYYY',
  };

  @action
  captureElement(elem) {
    //console.log('capturing element', elem);
    this.element = elem;
  }

  @action
  testOffset() {
    //console.log('testing offset');
    let element = this.element;
    let child = element.children[1];
    //console.log('element = ', element);
    //console.log('element.offsetTop = ', element.offsetTop);
    //console.log('element.scrollTop = ', element.scrollTop);

    //console.log('child = ', child);
    //console.log('child.offsetTop = ', child.offsetTop);
    //console.log('child.scrollTop = ', child.scrollTop);

    let parent = element.parentNode;
    //console.log('parent = ', parent);
    //console.log('parent.offsetTop = ', parent.offsetTop);
    //console.log('parent.scrollTop = ', parent.scrollTop);

    parent.scrollTop = child.offsetTop;
  }

  @union('args.comments', 'args.projectVersions', 'issueStateChanges') allItems;

  createdAtSorting = ['createdAtISO:desc'];
  @sort('allItems', 'createdAtSorting') sortedItems;

  get now() {
    return new Date();
  }

  get issueStateChanges() {
    let changes = [];
    for (const comment of this.args.comments.slice()) {
      changes.push(...comment.issueStateChanges);
    }
    return changes;
  }

  buildActivityData = task(async () => {
    //console.log('calling buildActivityData', this.sortedItems);
    let newActivityData = A([]);
    let placedItems = {};

    for (const item of this.sortedItems) {
      let topLevelItemForItem = this.getTopLevelItemForItem(item);
      //console.log(item, topLevelItemForItem);

      //******************************************************************
      // TODO: How do we end up with things that don't have a project attached?
      // We need to figure out the answer to that question. In the mean time
      // these blocks prevent rendering errors in the activity feed when we
      // end up with things with no project.
      if (!topLevelItemForItem) {
        console.error('we could not find a topLevelItemForItem', item);
        continue;
      }
      if (!item.project) {
        let peekedProject = this.store.peekRecord('project', item.projectId);
        if (peekedProject) {
          console.warn('we had to fallback to peekRecord for item', item);
          item.peekedProject = peekedProject;
        } else {
          console.error('we are missing the project for item', item);
          continue;
        }
      }
      if (!topLevelItemForItem.project) {
        let peekedProject = this.store.peekRecord(
          'project',
          topLevelItemForItem.projectId,
        );
        if (peekedProject) {
          console.warn(
            'we had to fallback to peekRecord for topLevelItemForItem',
            topLevelItemForItem,
            'for item',
            item,
          );
          topLevelItemForItem.peekedProject = peekedProject;
        } else {
          console.error(
            'we are missing the project for topLevelItemForItem',
            topLevelItemForItem,
            'with item = ',
            item,
          );
          continue;
        }
      }
      // END TODO
      //******************************************************************

      let topLevelItemKey =
        topLevelItemForItem.constructor.modelName +
        '-' +
        topLevelItemForItem.id;

      let topLevelItemData = placedItems[topLevelItemKey];
      if (!topLevelItemData) {
        //console.log('placing new topLevelItem')
        topLevelItemData = {
          id: topLevelItemKey,
          item: topLevelItemForItem,
          latestActivityTimestamp: item.createdAtISO,
        };
        placedItems[topLevelItemKey] = topLevelItemData;
        //newActivityData.push(topLevelItemData)

        // Now find the day based on this item (not topLevelItem),
        // because the item should be the most recent activity on the topLevelItem
        dayjs.extend(calendar)
        dayjs.extend(advancedFormat);
        let dayString = dayjs(item.createdAtISO).format('YYYY-MM-DD');
        let friendlyDayString = dayjs(item.createdAtISO).calendar(
          dayjs(),
          this.compactCalendarFormatHash,
        )

        let dayData = newActivityData.findBy('id', dayString);

        if (!dayData) {
          dayData = ActivityDay.create({ day: dayString });
          newActivityData.pushObject(dayData);
        }

        dayData.friendlyDayString = friendlyDayString;

        // Set a timestamp that we can use for sorting
        topLevelItemForItem.set('latestActivityTimestamp', item.createdAtISO);
        // Now push the top level item into the day for which there is the most recent activity
        dayData.addItem(topLevelItemForItem);
      } else {
        //console.info('not placing new topLevelItem')
      }

      //console.log('topLevelItemForItem.latestActivityTimestamp',topLevelItemForItem.latestActivityTimestamp,' -- ', item.createdAtISO, topLevelItemForItem.latestActivityTimestamp < item.createdAtISO);
      if (topLevelItemForItem.latestActivityTimestamp < item.createdAtISO) {
        topLevelItemForItem.latestActivityTimestamp = item.createdAtISO;
      }
    }
    //console.log('activity list data =', newActivityData);
    this.activityData = newActivityData;
  });

  getTopLevelItemForItem(item) {
    let topLevelItemForItem = item;
    //console.log('looking for ', item.constructor.modelName, item.id)
    if (item.type == 'state-change') {
      topLevelItemForItem = item.issue;
    } else if (item.constructor.modelName == 'comment') {
      if (item.type == 'issue') {
        topLevelItemForItem = item;
      } else if (item.bounce) {
        topLevelItemForItem = item;
      } else if (item.comment) {
        let upperComment = item.comment;
        if (upperComment.bounce) {
          topLevelItemForItem = item.comment;
        } else if (upperComment.type == 'issue') {
          topLevelItemForItem = upperComment;
        } else {
          topLevelItemForItem = upperComment.projectVersion;
        }
      } else {
        topLevelItemForItem = item.projectVersion;
      }
    }
    return topLevelItemForItem;
  }
}
